/**
 * Lab Worksheet — local scratch space for capturing tabular data.
 * Paste tab-delimited values (Excel default clipboard format), optionally
 * paste a parallel formulas grid (Excel: Ctrl+` → Copy), name the tab, and
 * accumulate as many sheets as needed. All data lives in this browser's
 * localStorage only — nothing is uploaded.
 *
 * Export reconstructs a workbook using Microsoft SpreadsheetML 2003 XML.
 * Excel and LibreOffice open the file natively (.xls extension). Formulas
 * round-trip if a formulas grid was supplied.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  FileSpreadsheet, Plus, Trash2, Download, Copy, Edit3, FileText,
  ClipboardPaste, Layers, AlertCircle, CheckCircle2, Cloud, CloudUpload, FolderOpen, X,
} from 'lucide-react';
import { toast as _toast } from 'sonner';
import PageHeader from '@/components/PageHeader';
import {
  apiSaveLabWorksheet, apiListLabWorksheets, apiGetLabWorksheet, apiDeleteLabWorksheet,
  type LabWorkbookSummary,
} from '@/lib/phpApi';

interface Sheet {
  id: string;
  name: string;
  values: string[][];
  formulas: string[][];
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const LS_KEY = 'rb_lab_worksheet_v1';
const MAX_PREVIEW_ROWS = 50;
const MAX_PREVIEW_COLS = 30;

function parseTSV(text: string): string[][] {
  const trimmed = text.replace(/\r\n?$/, '').replace(/\n$/, '');
  if (!trimmed) return [];
  return trimmed.split(/\r\n|\r|\n/).map(r => r.split('\t'));
}

function colLetter(n: number): string {
  let s = '';
  let x = n;
  while (x >= 0) {
    s = String.fromCharCode((x % 26) + 65) + s;
    x = Math.floor(x / 26) - 1;
  }
  return s;
}

function escapeXml(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function isNumber(v: string): boolean {
  if (v === '' || v == null) return false;
  const n = Number(v);
  return !Number.isNaN(n) && Number.isFinite(n);
}

/** Bijective base-26 column letters → 0-based column index. A→0, Z→25, AA→26. */
function lettersToColIndex(letters: string): number {
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64); // 'A' = 65
  }
  return n - 1;
}

/**
 * Convert an A1-notation formula into the R1C1 notation that SpreadsheetML 2003
 * (ss:Formula) requires. References are resolved relative to the cell at
 * (baseR, baseC) — both 0-based. A1-style formulas left as-is cause Excel to
 * reject the formula and display the cached value instead, so this is what
 * makes exported formulas actually compute in Excel.
 */
function a1FormulaToR1C1(formula: string, baseR: number, baseC: number): string {
  // Match an optional $-anchored cell reference: $?COL$?ROW. Guard both sides so
  // function names aren't mangled: a leading letter/digit/_/$ disqualifies it, and
  // a trailing '(' or alphanumeric means it's a function call (e.g. LOG10(), ATAN2())
  // or not a bare reference — never a real A1 cell ref.
  const refRe = /(?<![A-Za-z0-9_$])(\$?)([A-Za-z]{1,3})(\$?)(\d+)(?![A-Za-z0-9(])/g;
  return formula.replace(refRe, (_m, colAbs: string, colLetters: string, rowAbs: string, rowDigits: string) => {
    const targetCol = lettersToColIndex(colLetters.toUpperCase());
    const targetRow = parseInt(rowDigits, 10) - 1;
    const rowPart = rowAbs
      ? `R${targetRow + 1}`
      : (targetRow - baseR === 0 ? 'R' : `R[${targetRow - baseR}]`);
    const colPart = colAbs
      ? `C${targetCol + 1}`
      : (targetCol - baseC === 0 ? 'C' : `C[${targetCol - baseC}]`);
    return rowPart + colPart;
  });
}

function buildSpreadsheetML(sheets: Sheet[]): string {
  const lines: string[] = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push('<?mso-application progid="Excel.Sheet"?>');
  lines.push('<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"');
  lines.push(' xmlns:o="urn:schemas-microsoft-com:office:office"');
  lines.push(' xmlns:x="urn:schemas-microsoft-com:office:excel"');
  lines.push(' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">');
  lines.push('  <Styles>');
  lines.push('    <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Top"/></Style>');
  lines.push('  </Styles>');

  for (const sheet of sheets) {
    const safeName = sheet.name.replace(/[\[\]\*\/\\\?:]/g, '_').slice(0, 31) || 'Sheet';
    lines.push(`  <Worksheet ss:Name="${escapeXml(safeName)}">`);
    lines.push('    <Table>');
    for (let r = 0; r < sheet.values.length; r++) {
      const row = sheet.values[r];
      lines.push('      <Row>');
      for (let c = 0; c < row.length; c++) {
        const val = row[c] ?? '';
        const fml = sheet.formulas?.[r]?.[c]?.trim() ?? '';
        const cellAttrs: string[] = [];
        if (fml) {
          const body = fml.startsWith('=') ? fml.slice(1) : fml;
          const fmlR1C1 = '=' + a1FormulaToR1C1(body, r, c);
          cellAttrs.push(`ss:Formula="${escapeXml(fmlR1C1)}"`);
        }
        const cellOpen = cellAttrs.length ? `<Cell ${cellAttrs.join(' ')}>` : '<Cell>';
        if (val === '') {
          if (fml) lines.push(`        ${cellOpen}<Data ss:Type="String"></Data></Cell>`);
          else lines.push('        <Cell/>');
        } else if (isNumber(val)) {
          lines.push(`        ${cellOpen}<Data ss:Type="Number">${escapeXml(val)}</Data></Cell>`);
        } else {
          lines.push(`        ${cellOpen}<Data ss:Type="String">${escapeXml(val)}</Data></Cell>`);
        }
      }
      lines.push('      </Row>');
    }
    lines.push('    </Table>');
    lines.push('  </Worksheet>');
  }
  lines.push('</Workbook>');
  return lines.join('\n');
}

function buildCSV(sheet: Sheet): string {
  const esc = (v: string) => {
    if (v == null) return '';
    if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
    return v;
  };
  return sheet.values.map(row => row.map(esc).join(',')).join('\r\n');
}

function downloadBlob(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime + ';charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function loadSheets(): Sheet[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveSheets(sheets: Sheet[]) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(sheets)); }
  catch (e) { console.error('Failed to persist sheets', e); }
}

export default function LabWorksheetPage() {
  const [sheets, setSheets] = useState<Sheet[]>(loadSheets);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftPaste, setDraftPaste] = useState('');
  const [draftFormulas, setDraftFormulas] = useState('');
  const [showFormulasInput, setShowFormulasInput] = useState(false);
  const [editingCell, setEditingCell] = useState<{ r: number; c: number; kind: 'value' | 'formula' } | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const pasteAreaRef = useRef<HTMLTextAreaElement>(null);

  /* Cloud state */
  const [workbookName, setWorkbookName] = useState('Workbook');
  const [cloudId, setCloudId] = useState<number | null>(null);
  const [cloudSaving, setCloudSaving] = useState(false);
  const [cloudSavedAt, setCloudSavedAt] = useState<string | null>(null);
  const [cloudJustSaved, setCloudJustSaved] = useState(false);        // 3-second green flash after a successful save
  const [cloudSnapshot, setCloudSnapshot] = useState<string>('');     // serialised sheets at last save; used to detect unsaved changes
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cloudList, setCloudList] = useState<LabWorkbookSummary[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  // Dirty detection — true if user has modified sheets/name since last cloud save
  const isDirty = useMemo(() => {
    if (cloudId === null) return sheets.length > 0;
    return JSON.stringify({ n: workbookName, s: sheets }) !== cloudSnapshot;
  }, [cloudId, cloudSnapshot, sheets, workbookName]);

  const refreshCloudList = async () => {
    setPickerLoading(true);
    try { setCloudList(await apiListLabWorksheets()); }
    catch (e: any) { toast.error(`Cloud list failed: ${e?.message ?? e}`); }
    finally { setPickerLoading(false); }
  };

  const handleCloudSave = async () => {
    setCloudSaving(true);
    try {
      const res = await apiSaveLabWorksheet({
        id: cloudId ?? undefined,
        name: workbookName.trim() || 'Workbook',
        sheets,
      });
      setCloudId(res.id);
      setCloudSavedAt(new Date().toLocaleString());
      setCloudSnapshot(JSON.stringify({ n: workbookName, s: sheets }));
      setCloudJustSaved(true);
      setTimeout(() => setCloudJustSaved(false), 3500);
      toast.success(`Saved to cloud · workbook id ${res.id} · ${res.sheetCount} sheet${res.sheetCount === 1 ? '' : 's'} · ${res.cellCount.toLocaleString()} cell${res.cellCount === 1 ? '' : 's'}`);
    } catch (e: any) {
      toast.error(`Cloud save failed: ${e?.message ?? e}`);
    } finally {
      setCloudSaving(false);
    }
  };

  const handleCloudLoad = async (id: number) => {
    setPickerLoading(true);
    try {
      const wb = await apiGetLabWorksheet(id);
      setSheets(wb.sheets || []);
      setWorkbookName(wb.name || 'Workbook');
      setCloudId(wb.id);
      setCloudSavedAt(new Date(wb.updated_at).toLocaleString());
      // Snapshot matches what we just loaded → not dirty
      setCloudSnapshot(JSON.stringify({ n: wb.name || 'Workbook', s: wb.sheets || [] }));
      setPickerOpen(false);
      toast.success(`Loaded "${wb.name}" — ${(wb.sheets || []).length} sheets`);
    } catch (e: any) {
      toast.error(`Cloud load failed: ${e?.message ?? e}`);
    } finally {
      setPickerLoading(false);
    }
  };

  const handleCloudDelete = async (id: number, name: string) => {
    if (!confirm(`Delete cloud workbook "${name}"? This cannot be undone.`)) return;
    try {
      await apiDeleteLabWorksheet(id);
      toast.success(`Deleted "${name}"`);
      if (cloudId === id) setCloudId(null);
      refreshCloudList();
    } catch (e: any) {
      toast.error(`Delete failed: ${e?.message ?? e}`);
    }
  };

  useEffect(() => { saveSheets(sheets); }, [sheets]);
  useEffect(() => {
    if (editingCell && editInputRef.current) editInputRef.current.focus();
  }, [editingCell]);

  const activeSheet = useMemo(() => sheets.find(s => s.id === activeId) ?? null, [sheets, activeId]);

  const handleAddSheet = () => {
    const name = draftName.trim() || `Sheet${sheets.length + 1}`;
    if (!draftPaste.trim()) { toast.error('Paste some data first'); return; }
    const values = parseTSV(draftPaste);
    const formulas: string[][] = values.map(row => row.map(() => ''));
    if (showFormulasInput && draftFormulas.trim()) {
      const f = parseTSV(draftFormulas);
      for (let r = 0; r < values.length; r++) {
        for (let c = 0; c < values[r].length; c++) {
          formulas[r][c] = f[r]?.[c] ?? '';
        }
      }
    }
    const now = new Date().toISOString();
    const sheet: Sheet = {
      id: `sht_${Date.now()}`, name, values, formulas, notes: '',
      createdAt: now, updatedAt: now,
    };
    setSheets([...sheets, sheet]);
    setActiveId(sheet.id);
    setDraftName(''); setDraftPaste(''); setDraftFormulas(''); setShowFormulasInput(false);
    toast.success(`Added "${name}" — ${values.length} rows × ${values[0]?.length || 0} cols`);
  };

  const handleDeleteSheet = (id: string) => {
    if (!confirm('Delete this tab? This cannot be undone.')) return;
    setSheets(sheets.filter(s => s.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const handleRenameSheet = (id: string, name: string) => {
    setSheets(sheets.map(s => s.id === id ? { ...s, name, updatedAt: new Date().toISOString() } : s));
  };

  const handleUpdateCell = (sheetId: string, r: number, c: number, kind: 'value' | 'formula', newVal: string) => {
    setSheets(sheets.map(s => {
      if (s.id !== sheetId) return s;
      const grid = kind === 'value' ? s.values.map(row => [...row]) : s.formulas.map(row => [...row]);
      if (!grid[r]) grid[r] = [];
      grid[r][c] = newVal;
      return {
        ...s,
        [kind === 'value' ? 'values' : 'formulas']: grid,
        updatedAt: new Date().toISOString(),
      };
    }));
  };

  const handleUpdateNotes = (sheetId: string, notes: string) => {
    setSheets(sheets.map(s => s.id === sheetId ? { ...s, notes, updatedAt: new Date().toISOString() } : s));
  };

  const handleExportWorkbook = () => {
    if (sheets.length === 0) { toast.error('No sheets to export'); return; }
    const xml = buildSpreadsheetML(sheets);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadBlob(`workbook-${stamp}.xls`, xml, 'application/vnd.ms-excel');
    toast.success(`Exported ${sheets.length} sheet${sheets.length === 1 ? '' : 's'} → workbook-${stamp}.xls`);
  };

  const handleExportSheetCSV = (sheet: Sheet) => {
    downloadBlob(`${sheet.name}.csv`, buildCSV(sheet), 'text/csv');
  };

  const handleCopyTSV = async (sheet: Sheet) => {
    const tsv = sheet.values.map(row => row.join('\t')).join('\r\n');
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success('Copied TSV to clipboard');
    } catch {
      toast.error('Clipboard write blocked');
    }
  };

  const handleClearAll = () => {
    if (!confirm(`Clear ALL ${sheets.length} sheets? This cannot be undone.`)) return;
    setSheets([]); setActiveId(null);
    toast.success('All sheets cleared');
  };

  const totalCells = sheets.reduce((sum, s) => sum + s.values.reduce((c, row) => c + row.length, 0), 0);

  return (
    <div className="space-y-5 px-3 py-4">
      <PageHeader
        title="Lab Worksheet"
        subtitle="Local scratch space for tabular data — paste sheets from any spreadsheet, edit cells, attach formulas, then export as one workbook. Browser-only storage."
        icon={FileSpreadsheet}
        actions={
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleCloudSave}
              disabled={cloudSaving || sheets.length === 0}
              className={`relative flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold border-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all ${
                cloudJustSaved
                  ? 'bg-emerald-500/25 text-emerald-200 border-emerald-400/70 shadow-[0_0_18px_rgba(16,185,129,0.55)]'
                  : isDirty && cloudId
                    ? 'bg-amber-500/15 text-amber-300 border-amber-400/50 hover:bg-amber-500/25 animate-pulse'
                    : 'bg-purple-500/10 text-purple-300 border-purple-500/30 hover:bg-purple-500/20'
              }`}
              title={cloudJustSaved ? 'Just saved to database' : isDirty && cloudId ? 'Unsaved changes — click to push to database' : cloudId ? 'In sync with database' : 'Send this workbook to the database'}
            >
              <CloudUpload className="w-3.5 h-3.5" />
              {cloudSaving
                ? 'Saving…'
                : cloudJustSaved
                  ? `✓ Saved to DB (id #${cloudId})`
                  : isDirty && cloudId
                    ? `Update cloud (unsaved changes)`
                    : cloudId
                      ? `✓ In sync (id #${cloudId})`
                      : 'Save to cloud'}
            </button>
            <button
              onClick={() => { setPickerOpen(true); refreshCloudList(); }}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"
            >
              <FolderOpen className="w-3.5 h-3.5" />Open from cloud
            </button>
            <button
              onClick={handleExportWorkbook}
              disabled={sheets.length === 0}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Download className="w-3.5 h-3.5" />Export .xls
            </button>
            <button
              onClick={handleClearAll}
              disabled={sheets.length === 0}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-rose-500/10 text-rose-400 text-xs font-bold border border-rose-500/20 hover:bg-rose-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" />Clear local
            </button>
          </div>
        }
      />

      <div className="mb-2 text-center">
        <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
          Reynolds &amp; Bauhm Smart Tool 2.0 — Local Worksheet — All Rights Reserved
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
        <div className="md:col-span-2 p-3 rb-card">
          <label className="text-[10px] text-slate-500 uppercase">Workbook name</label>
          <input value={workbookName} onChange={e => setWorkbookName(e.target.value)} placeholder="My workbook"
            className="w-full mt-1 px-3 py-1.5 bg-rb-elevated border border-slate-800 rounded-lg text-sm font-bold text-white" />
        </div>
        <div className="p-3 rb-inner-card">
          <p className="text-[10px] text-slate-500 uppercase">Sheets</p>
          <p className="text-lg font-bold text-white">{sheets.length}</p>
        </div>
        <div className="p-3 rb-inner-card">
          <p className="text-[10px] text-slate-500 uppercase">Total cells</p>
          <p className="text-lg font-bold text-white">{totalCells.toLocaleString()}</p>
        </div>
        <div className={`p-3 rb-inner-card transition-all ${
          cloudJustSaved ? 'border-emerald-400/70 shadow-[0_0_22px_rgba(16,185,129,0.4)]'
          : isDirty && cloudId ? 'border-amber-500/50'
          : cloudId ? 'border-emerald-500/40'
          : 'border-amber-500/40'
        }`}>
          <p className="text-[10px] text-slate-500 uppercase flex items-center gap-2">
            Cloud status
            {/* live status dot */}
            <span className="relative inline-flex h-2 w-2">
              {cloudId && !isDirty && (
                <>
                  <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                  <span className="relative rounded-full bg-emerald-400 w-2 h-2 shadow-[0_0_6px_rgba(16,185,129,0.9)]" />
                </>
              )}
              {cloudId && isDirty && (
                <span className="relative rounded-full bg-amber-400 w-2 h-2 shadow-[0_0_6px_rgba(251,191,36,0.9)] animate-pulse" />
              )}
              {!cloudId && (
                <span className="relative rounded-full bg-rose-400 w-2 h-2 shadow-[0_0_6px_rgba(244,63,94,0.9)]" />
              )}
            </span>
          </p>
          {cloudJustSaved && (
            <p className="text-sm font-bold text-emerald-300 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" />Saved to database
            </p>
          )}
          {!cloudJustSaved && cloudId && !isDirty && (
            <>
              <p className="text-sm font-bold text-emerald-400 flex items-center gap-1.5">
                <Cloud className="w-3.5 h-3.5" />In DB · id #{cloudId}
              </p>
              <p className="text-[10px] text-emerald-500/70">last save: {cloudSavedAt ?? '—'}</p>
            </>
          )}
          {!cloudJustSaved && cloudId && isDirty && (
            <>
              <p className="text-sm font-bold text-amber-400 flex items-center gap-1.5">
                <Cloud className="w-3.5 h-3.5" />id #{cloudId} · unsaved
              </p>
              <p className="text-[10px] text-amber-300/80">Click "Update cloud" to sync</p>
            </>
          )}
          {!cloudId && (
            <>
              <p className="text-sm font-bold text-rose-400">Not in database</p>
              <p className="text-[10px] text-slate-500">Click "Save to cloud" to persist</p>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-4 space-y-4">

          <div className="p-4 rb-card space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <ClipboardPaste className="w-4 h-4 text-sky-400" />New tab from paste
            </h3>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Tab name (optional)</label>
              <input
                type="text"
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                placeholder="e.g. Inputs, Q1 Data, Summary"
                className="w-full mt-1 px-3 py-1.5 bg-rb-elevated border border-slate-800 rounded-lg text-xs text-slate-200 placeholder-slate-600"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Values (paste — tabs as columns, newlines as rows)</label>
              <textarea
                ref={pasteAreaRef}
                value={draftPaste}
                onChange={e => setDraftPaste(e.target.value)}
                placeholder="Click here, then Ctrl+V from your spreadsheet…"
                rows={10}
                className="w-full mt-1 px-3 py-2 bg-rb-elevated border border-slate-800 rounded-lg text-xs text-slate-200 placeholder-slate-600 font-mono"
                onPaste={() => setTimeout(() => {
                  if (pasteAreaRef.current) {
                    const t = pasteAreaRef.current.value;
                    const rows = t.split(/\r\n|\r|\n/);
                    const cols = rows[0]?.split('\t').length || 0;
                    toast.info(`${rows.length} rows × ${cols} cols pasted`);
                  }
                }, 100)}
              />
            </div>

            <button
              type="button"
              onClick={() => setShowFormulasInput(!showFormulasInput)}
              className="text-[10px] text-sky-400 hover:text-sky-300"
            >
              {showFormulasInput ? '− Hide formulas paste' : '+ Add formulas (optional, from Excel Ctrl+`)'}
            </button>
            {showFormulasInput && (
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Formulas grid (same shape — paste after Ctrl+` in Excel, then Ctrl+C)</label>
                <textarea
                  value={draftFormulas}
                  onChange={e => setDraftFormulas(e.target.value)}
                  placeholder={'e.g.\t=B2*1.2\t=SUM(A:A)'}
                  rows={6}
                  className="w-full mt-1 px-3 py-2 bg-rb-elevated border border-slate-800 rounded-lg text-xs text-slate-200 placeholder-slate-600 font-mono"
                />
                <p className="text-[10px] text-slate-600 mt-1">Tip: in Excel press Ctrl+` (backtick) to toggle "Show Formulas", select the same range, Ctrl+C, paste here. Empty cells = no formula.</p>
              </div>
            )}

            <button
              onClick={handleAddSheet}
              disabled={!draftPaste.trim()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Plus className="w-3.5 h-3.5" />Add tab
            </button>
          </div>

          <div className="p-4 rb-card space-y-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-amber-400" />Tabs ({sheets.length})
            </h3>
            {sheets.length === 0 && (
              <p className="text-xs text-slate-500">No tabs yet. Paste your first range above.</p>
            )}
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {sheets.map(s => {
                const active = s.id === activeId;
                const rows = s.values.length;
                const cols = s.values[0]?.length || 0;
                return (
                  <div
                    key={s.id}
                    className={`p-2 rounded-lg border transition-all ${active ? 'bg-sky-500/10 border-sky-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button onClick={() => setActiveId(s.id)} className="flex-1 text-left min-w-0">
                        <p className={`text-xs font-bold truncate ${active ? 'text-sky-400' : 'text-slate-200'}`}>{s.name}</p>
                        <p className="text-[10px] text-slate-500">{rows} × {cols} cells</p>
                      </button>
                      <button onClick={() => handleDeleteSheet(s.id)} className="p-1 rounded hover:bg-rose-500/20 text-rose-400" title="Delete tab">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-4 rb-card-sm space-y-2">
            <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-slate-400" />How to use
            </h3>
            <ol className="text-[11px] text-slate-400 space-y-1 list-decimal list-inside leading-relaxed">
              <li>In your spreadsheet, select a range and Ctrl+C.</li>
              <li>Paste into the textarea, name the tab, click "Add tab".</li>
              <li>(Optional) Toggle "Show Formulas" in Excel (Ctrl+`), copy again, paste into the formulas box.</li>
              <li>Repeat for each sheet.</li>
              <li>Click "Export workbook" — get a single .xls file with all tabs and formulas.</li>
            </ol>
            <p className="text-[10px] text-slate-500 leading-relaxed">
              Data lives in this browser only. Clear with the button above or by clearing site data in browser settings.
            </p>
          </div>
        </div>

        <div className="xl:col-span-8 space-y-4">
          {!activeSheet && (
            <div className="p-8 rb-card text-center">
              <FileSpreadsheet className="w-12 h-12 text-slate-700 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Select a tab from the left, or paste a new one to get started.</p>
            </div>
          )}

          {pickerOpen && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                 onClick={() => setPickerOpen(false)}>
              <div className="rb-card w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
                   onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-slate-800">
                  <div>
                    <h3 className="text-sm font-bold text-white flex items-center gap-2"><Cloud className="w-4 h-4 text-purple-400" />Cloud workbooks</h3>
                    <p className="text-[10px] text-slate-500">{cloudList.length} saved {cloudList.length === 1 ? 'workbook' : 'workbooks'}</p>
                  </div>
                  <button onClick={() => setPickerOpen(false)} className="p-1 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="overflow-y-auto p-3 space-y-2">
                  {pickerLoading && <p className="text-xs text-slate-500 text-center py-6">Loading…</p>}
                  {!pickerLoading && cloudList.length === 0 && <p className="text-xs text-slate-500 text-center py-6">No cloud workbooks yet. Save your current local set first.</p>}
                  {cloudList.map(wb => (
                    <div key={wb.id} className="p-3 rb-inner-card flex items-center justify-between gap-3 hover:bg-slate-800/30">
                      <button onClick={() => handleCloudLoad(wb.id)} className="flex-1 text-left min-w-0">
                        <p className="text-xs font-bold text-white truncate">{wb.name}</p>
                        <p className="text-[10px] text-slate-500">
                          id #{wb.id} · {wb.sheet_count} sheets · {wb.cell_count.toLocaleString()} cells · updated {new Date(wb.updated_at).toLocaleString()}
                        </p>
                      </button>
                      <button onClick={() => handleCloudDelete(wb.id, wb.name)} className="p-2 rounded text-rose-400 hover:bg-rose-500/20" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {activeSheet && <ActiveSheetView
            sheet={activeSheet}
            onRename={(name) => handleRenameSheet(activeSheet.id, name)}
            onUpdateCell={(r, c, kind, val) => handleUpdateCell(activeSheet.id, r, c, kind, val)}
            onUpdateNotes={(notes) => handleUpdateNotes(activeSheet.id, notes)}
            onExportCSV={() => handleExportSheetCSV(activeSheet)}
            onCopyTSV={() => handleCopyTSV(activeSheet)}
            editingCell={editingCell}
            setEditingCell={setEditingCell}
            editInputRef={editInputRef}
          />}
        </div>
      </div>
    </div>
  );
}

interface ActiveSheetViewProps {
  sheet: Sheet;
  onRename: (name: string) => void;
  onUpdateCell: (r: number, c: number, kind: 'value' | 'formula', val: string) => void;
  onUpdateNotes: (notes: string) => void;
  onExportCSV: () => void;
  onCopyTSV: () => void;
  editingCell: { r: number; c: number; kind: 'value' | 'formula' } | null;
  setEditingCell: (c: { r: number; c: number; kind: 'value' | 'formula' } | null) => void;
  editInputRef: React.RefObject<HTMLInputElement | null>;
}

function ActiveSheetView(props: ActiveSheetViewProps) {
  const { sheet, onRename, onUpdateCell, onUpdateNotes, onExportCSV, onCopyTSV, editingCell, setEditingCell, editInputRef } = props;
  const [renameMode, setRenameMode] = useState(false);
  const [nameDraft, setNameDraft] = useState(sheet.name);
  const [showFormulas, setShowFormulas] = useState(false);

  useEffect(() => { setNameDraft(sheet.name); }, [sheet.id, sheet.name]);

  const rows = sheet.values;
  const formulas = sheet.formulas || [];
  const numCols = rows.reduce((max, r) => Math.max(max, r.length), 0);
  const hasAnyFormulas = formulas.some(r => r?.some(c => c && c.trim() !== ''));
  const previewRows = rows.slice(0, MAX_PREVIEW_ROWS);
  const truncatedRows = rows.length - previewRows.length;
  const truncatedCols = Math.max(0, numCols - MAX_PREVIEW_COLS);

  const commitEdit = (r: number, c: number, kind: 'value' | 'formula', val: string) => {
    onUpdateCell(r, c, kind, val);
    setEditingCell(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {renameMode ? (
            <input
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value)}
              onBlur={() => { onRename(nameDraft.trim() || sheet.name); setRenameMode(false); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { onRename(nameDraft.trim() || sheet.name); setRenameMode(false); }
                if (e.key === 'Escape') { setNameDraft(sheet.name); setRenameMode(false); }
              }}
              autoFocus
              className="text-sm font-bold bg-rb-elevated border border-sky-500/30 rounded px-2 py-0.5 text-white"
            />
          ) : (
            <button onClick={() => setRenameMode(true)} className="flex items-center gap-2 text-sm font-bold text-white hover:text-sky-400 transition-colors min-w-0" title="Rename tab">
              <span className="truncate">{sheet.name}</span>
              <Edit3 className="w-3 h-3 opacity-50" />
            </button>
          )}
          <span className="text-[10px] text-slate-500">{rows.length} × {numCols}</span>
          {hasAnyFormulas && (
            <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded">formulas</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasAnyFormulas && (
            <button onClick={() => setShowFormulas(!showFormulas)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 text-[11px] font-bold border border-amber-500/20 hover:bg-amber-500/20">
              {showFormulas ? 'Show values' : 'Show formulas'}
            </button>
          )}
          <button onClick={onCopyTSV} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-500/10 text-sky-400 text-[11px] font-bold border border-sky-500/20 hover:bg-sky-500/20">
            <Copy className="w-3 h-3" />Copy TSV
          </button>
          <button onClick={onExportCSV} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-[11px] font-bold border border-emerald-500/20 hover:bg-emerald-500/20">
            <Download className="w-3 h-3" />.csv
          </button>
        </div>
      </div>

      <div className="rb-card overflow-auto max-h-[60vh]">
        <table className="text-[11px] border-collapse">
          <thead>
            <tr>
              <th className="bg-rb-elevated border border-slate-800 px-1.5 py-1 text-slate-600 text-[10px] sticky top-0 left-0 z-20">#</th>
              {Array.from({ length: Math.min(numCols, MAX_PREVIEW_COLS) }).map((_, c) => (
                <th key={c} className="bg-rb-elevated border border-slate-800 px-2 py-1 text-slate-500 font-mono text-[10px] sticky top-0 z-10 min-w-[80px]">
                  {colLetter(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, r) => (
              <tr key={r}>
                <td className="bg-rb-elevated border border-slate-800 px-1.5 py-0.5 text-slate-600 text-[10px] sticky left-0 z-10 text-center">{r + 1}</td>
                {Array.from({ length: Math.min(numCols, MAX_PREVIEW_COLS) }).map((_, c) => {
                  const val = row[c] ?? '';
                  const fml = formulas[r]?.[c] ?? '';
                  const display = showFormulas && fml ? fml : val;
                  const editing = editingCell && editingCell.r === r && editingCell.c === c && editingCell.kind === (showFormulas ? 'formula' : 'value');
                  return (
                    <td
                      key={c}
                      className="border border-slate-800 px-2 py-0.5 align-top cursor-text hover:bg-sky-500/5"
                      onDoubleClick={() => setEditingCell({ r, c, kind: showFormulas ? 'formula' : 'value' })}
                      title={fml ? `Formula: ${fml}` : ''}
                    >
                      {editing ? (
                        <input
                          ref={editInputRef}
                          defaultValue={display}
                          onBlur={(e) => commitEdit(r, c, showFormulas ? 'formula' : 'value', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit(r, c, showFormulas ? 'formula' : 'value', (e.target as HTMLInputElement).value);
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                          className="w-full bg-slate-900 border border-sky-500/40 px-1 text-[11px] text-white outline-none"
                        />
                      ) : (
                        <span className={showFormulas && fml ? 'text-amber-400 font-mono' : isNumber(val) ? 'text-cyan-300 tabular-nums' : 'text-slate-200'}>
                          {display || ' '}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(truncatedRows > 0 || truncatedCols > 0) && (
        <div className="p-2 rb-inner-card text-[11px] text-amber-400 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5" />
          Preview limited to {MAX_PREVIEW_ROWS} rows × {MAX_PREVIEW_COLS} cols.
          {truncatedRows > 0 && <> {truncatedRows} more rows hidden.</>}
          {truncatedCols > 0 && <> {truncatedCols} more cols hidden.</>}
          {' '}Full data is preserved in storage and in exports.
        </div>
      )}

      <div className="p-3 rb-inner-card space-y-2">
        <label className="text-[10px] text-slate-500 uppercase">Notes (per tab)</label>
        <textarea
          value={sheet.notes}
          onChange={e => onUpdateNotes(e.target.value)}
          placeholder="Anything you want to remember about this sheet…"
          rows={2}
          className="w-full px-3 py-1.5 bg-rb-elevated border border-slate-800 rounded text-xs text-slate-200 placeholder-slate-600"
        />
      </div>

      <div className="text-[10px] text-slate-600 flex items-center gap-2">
        <CheckCircle2 className="w-3 h-3" />
        Updated {new Date(sheet.updatedAt).toLocaleString()}. Saved automatically.
      </div>
    </div>
  );
}
